/**
 * flatten() — Converts the declarative tree into the same shapes
 * that tool-registry.tsx consumers expect.
 *
 * Produces:
 *  - ToolSource[] (backward-compatible flat array)
 *  - NavSection[] (direct sidebar input — no buildNavSections() needed)
 *  - Set<string>  (all route paths — replaces manual PREFIXED_ROUTE_PATHS)
 */
import React from "react";
import type { DomainNode, GroupNode, ToolCategory, ToolNode } from "./builders";

/* ─── Output types (matching existing tool-registry exports) ─── */

export interface ToolSourceFlat {
  label: string;
  desc: string;
  to: string;
  icon: React.ReactNode;
  category: ToolCategory;
  domain: string;
  ai?: boolean;
  navGroup?: string;
  navLabel?: string;
  assistantIntents?: string[];
  assistantPrompt?: string;
}

export interface NavItemFlat {
  label: string;
  to: string;
  icon: React.ReactNode;
  category: ToolCategory;
  ai?: boolean;
  navigable?: boolean;
  children?: { label: string; to: string }[];
}

export interface NavSectionFlat {
  domain: string;
  items: NavItemFlat[];
}

export interface FlattenResult {
  toolSources: ToolSourceFlat[];
  navSections: NavSectionFlat[];
  routePaths: Set<string>;
}

/* ─── Helpers ─── */

function resolveCategory(
  opts: { category?: ToolCategory },
  fallback: ToolCategory,
): ToolCategory {
  return opts.category ?? fallback;
}

function makeIcon(
  Icon:
    | { new (...args: never[]): never }
    | ((...args: never[]) => React.ReactNode),
  className = "size-4",
): React.ReactNode {
  return React.createElement(
    Icon as React.ComponentType<{ className: string }>,
    { className },
  );
}

/* ─── Flatten logic ─── */

function flattenGroup(
  g: GroupNode,
  domainKey: string,
  sources: ToolSourceFlat[],
  items: NavItemFlat[],
  paths: Set<string>,
): void {
  const parentCategory = resolveCategory(g.opts, "smart");

  // Parent as ToolSource (no navGroup — it IS the parent)
  sources.push({
    label: g.label,
    desc: g.desc,
    to: g.to,
    icon: makeIcon(g.Icon),
    category: parentCategory,
    domain: domainKey,
    ai: g.opts.ai,
    assistantIntents: g.opts.assistantIntents,
    assistantPrompt: g.opts.assistantPrompt,
  });
  paths.add(g.to);

  // Children as ToolSource (with navGroup pointing to parent)
  const navChildren: { label: string; to: string }[] = [];
  for (const child of g.children) {
    const childCategory = resolveCategory(child.opts, parentCategory);
    sources.push({
      label: child.label,
      desc: child.desc,
      to: child.to,
      icon: makeIcon(child.Icon),
      category: childCategory,
      domain: domainKey,
      ai: child.opts.ai,
      navGroup: g.label,
      navLabel: child.navLabel,
      assistantIntents: child.opts.assistantIntents,
      assistantPrompt: child.opts.assistantPrompt,
    });
    paths.add(child.to);
    navChildren.push({ label: child.navLabel, to: child.to });
  }

  // NavItem with children
  items.push({
    label: g.label,
    to: g.to,
    icon: makeIcon(g.Icon),
    category: parentCategory,
    ai: g.opts.ai,
    navigable: true,
    children: navChildren,
  });
}

function flattenTool(
  t: ToolNode,
  domainKey: string,
  sources: ToolSourceFlat[],
  items: NavItemFlat[],
  paths: Set<string>,
): void {
  const category = resolveCategory(t.opts, "smart");
  sources.push({
    label: t.label,
    desc: t.desc,
    to: t.to,
    icon: makeIcon(t.Icon),
    category,
    domain: domainKey,
    ai: t.opts.ai,
    assistantIntents: t.opts.assistantIntents,
    assistantPrompt: t.opts.assistantPrompt,
  });
  paths.add(t.to);

  items.push({
    label: t.label,
    to: t.to,
    icon: makeIcon(t.Icon),
    category,
    ai: t.opts.ai,
  });
}

export function flatten(
  tree: DomainNode[],
  hiddenPaths: readonly string[] = [],
): FlattenResult {
  const toolSources: ToolSourceFlat[] = [];
  const navSections: NavSectionFlat[] = [];
  const routePaths = new Set<string>();

  for (const d of tree) {
    const items: NavItemFlat[] = [];

    for (const child of d.children) {
      if (child.kind === "group") {
        flattenGroup(child, d.key, toolSources, items, routePaths);
      } else {
        flattenTool(child, d.key, toolSources, items, routePaths);
      }
    }

    navSections.push({ domain: d.key, items });
  }

  // Add hidden route paths (routes that exist but aren't in sidebar)
  for (const p of hiddenPaths) {
    routePaths.add(p);
  }

  return { toolSources, navSections, routePaths };
}
